import React, { useState } from 'react';
import { Box, VStack, HStack, Text, Button, Heading, SimpleGrid, Spinner, Image, Modal, ModalOverlay, ModalContent, ModalBody, IconButton } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { supabase } from '../services/supabaseClient';
import { FaTimes } from 'react-icons/fa';

const MotionBox = motion(Box);

const GalleryScreen = ({ onBack }) => {
  const [styles, setStyles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Carregar categorias reais do Supabase
  React.useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase.from('categorias_estilos').select('*').order('nome');
      if (!error && data) {
        setCategories([{ id: null, nome: 'Todos' }, ...data]);
      } else {
        setCategories([{ id: null, nome: 'Todos' }]);
      }
    };
    fetchCategories();
  }, []);

  // Carregar estilos reais do Supabase
  React.useEffect(() => {
    const fetchStyles = async () => {
      setIsLoading(true);
      let query = supabase.from('galeria_estilos').select('id, nome, descricao, imagem_url, categoria:categorias_estilos(nome, id)');
      if (selectedCategory) {
        query = query.eq('categoria_id', selectedCategory);
      }
      const { data, error } = await query.order('created_at', { ascending: false });
      if (!error && data) {
        setStyles(data);
      } else {
        setStyles([]);
      }
      setIsLoading(false);
    };
    fetchStyles();
  }, [selectedCategory]);

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="black"
      overflowY="auto"
      padding={6}
    >
      <VStack spacing={6} align="stretch">
        {/* Header com título e botão voltar */}
        <Box
          position="sticky"
          top={0}
          zIndex={10}
          bg="linear-gradient(180deg, rgba(26,32,44,0.95) 0%, rgba(26,32,44,0.8) 100%)"
          backdropFilter="blur(10px)"
          px={4}
          py={3}
          mb={4}
          borderRadius="xl"
          boxShadow="lg"
        >
          <HStack justifyContent="space-between" alignItems="center" width="100%">
            <Heading size="lg" color="white">✂️ Galeria de Estilos</Heading>
            <Button
              variant="solid"
              onClick={onBack}
              bg="yellow.400"
              color="gray.800"
              _hover={{ bg: 'yellow.500' }}
              _active={{ bg: 'yellow.600' }}
              size="lg"
              borderRadius="xl"
              px={6}
            >
              Voltar
            </Button>
          </HStack>
        </Box>

        {/* Seletor de Categorias */}
        <Box
          p={6}
          bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          backdropFilter="blur(10px)"
        >
          <HStack mb={4} justify="space-between" align="center">
            <Heading size="md" color="yellow.400" letterSpacing="wide">
              Categorias
            </Heading>
          </HStack>
          
          <Box overflowX="auto" pb={2}>
            <HStack spacing={3}>
              {categories.map((category) => (
                <Button
                  key={category.id ?? 'all'}
                  onClick={() => setSelectedCategory(category.id)}
                  variant="solid"
                  bg={selectedCategory === category.id ? "yellow.400" : "whiteAlpha.200"}
                  color={selectedCategory === category.id ? "black" : "white"}
                  _hover={selectedCategory === category.id ? 
                    { bg: 'yellow.500' } : 
                    { bg: 'whiteAlpha.300', transform: 'translateY(-2px)' }
                  }
                  _active={selectedCategory === category.id ? 
                    { bg: 'yellow.600' } : 
                    { bg: 'whiteAlpha.400' }
                  }
                  transition="all 0.2s"
                  height="50px"
                  minWidth="120px"
                  borderRadius="xl"
                  fontSize="lg"
                  fontWeight="bold"
                >
                  {category.nome}
                </Button>
              ))}
            </HStack>
          </Box>
        </Box>

        {/* Grid de Estilos */}
        <Box
          p={6}
          bg="linear-gradient(180deg, rgba(45,55,72,0.9) 0%, rgba(26,32,44,0.9) 100%)"
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          backdropFilter="blur(10px)"
        >
          <HStack mb={6} justify="space-between" align="center">
            <Heading size="md" color="yellow.400" letterSpacing="wide">
              Estilos Disponíveis
            </Heading>
          </HStack>
          
          {isLoading ? (
            <VStack spacing={4} align="center" py={8}>
              <Spinner size="xl" color="yellow.400" thickness="4px" />
              <Text color="gray.400" fontSize="lg">Carregando estilos...</Text>
            </VStack>
          ) : styles.length === 0 ? (
            <VStack spacing={4} align="center" py={8}>
              <Text color="gray.400" fontSize="lg">Nenhum estilo encontrado</Text>
            </VStack>
          ) : (
            <SimpleGrid columns={2} spacing={4}>
              {styles.map((style) => (
                <MotionBox
                  key={style.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  bg="whiteAlpha.50"
                  _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                  borderRadius="xl"
                  boxShadow="lg"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  overflow="hidden"
                >
                  {/* Imagem real do estilo */}
                  <Box 
                    height="180px" 
                    bg="gray.700" 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                    overflow="hidden"
                  >
                    {style.imagem_url ? (
                      <Image src={style.imagem_url} alt={style.nome} height="100%" width="100%" objectFit="cover" borderRadius="md" />
                    ) : (
                      <Text color="whiteAlpha.700">{style.nome}</Text>
                    )}
                  </Box>
                  <Box p={4}>
                    <Text fontSize="lg" color="white" fontWeight="bold">{style.nome}</Text>
                    <Text fontSize="md" color="gray.300" mt={1}>{style.categoria?.nome || '-'}</Text>
                    {style.descricao && (
                      <Text fontSize="sm" color="gray.400" mt={2}>{style.descricao}</Text>
                    )}
                    <Button 
                      size="lg" 
                      mt={4}
                      width="100%"
                      bg="yellow.400"
                      color="black"
                      _hover={{ bg: "yellow.500", transform: 'translateY(-2px)' }}
                      _active={{ bg: "yellow.600" }}
                      transition="all 0.2s"
                      borderRadius="xl"
                      height="50px"
                      fontSize="lg"
                      fontWeight="bold"
                      onClick={() => {
                        setSelectedImage(style.imagem_url);
                        setIsImageModalOpen(true);
                      }}
                    >
                      Ver
                    </Button>
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>
          )}
        </Box>
      </VStack>

      {/* Modal para exibir a imagem expandida */}
      <Modal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} size="full" isCentered>
        <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="100vw" maxH="100vh" m={0} p={0}>
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" position="relative">
            <IconButton
              icon={<FaTimes size={24} />}
              aria-label="Fechar"
              position="absolute"
              top={4}
              right={4}
              zIndex={2}
              onClick={() => setIsImageModalOpen(false)}
              colorScheme="blackAlpha"
              bg="blackAlpha.700"
              color="white"
              size="lg"
              isRound
              _hover={{ bg: "blackAlpha.800" }}
              _active={{ bg: "blackAlpha.900" }}
            />
            {selectedImage && (
              <Box 
                maxW="90vw" 
                maxH="90vh" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                onClick={(e) => e.stopPropagation()}
              >
                <Image 
                  src={selectedImage} 
                  alt="Estilo ampliado" 
                  maxH="90vh" 
                  maxW="90vw" 
                  objectFit="contain" 
                  borderRadius="md"
                  boxShadow="dark-lg"
                />
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </MotionBox>
  );
};

export default GalleryScreen;
